import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva } from "class-variance-authority";

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-border text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow hover:bg-primary/90",
        destructive:
          "border-destructive bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/80",
        cancel:
          "border-rose-700 bg-rose-700 text-white shadow-sm hover:bg-rose-800 hover:text-white",
        outline:
          "border-input bg-primary/5 shadow-sm hover:bg-primary/10 hover:text-foreground",
        secondary:
          "border-primary/30 bg-primary/10 text-primary shadow-sm hover:bg-primary/15",
        ghost: "bg-primary/5 hover:bg-primary/10 hover:text-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-8 rounded-md px-3 text-xs",
        lg: "h-10 rounded-md px-8",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

const Button = React.forwardRef(({ className, variant, size, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : "button"
  const isCancel = React.Children.toArray(props.children)
    .some((child) => typeof child === "string" && child.trim().toLowerCase() === "cancel");
  const resolvedVariant = isCancel ? "cancel" : variant;
  return (
    <Comp
      data-cancel={isCancel ? "true" : undefined}
      className={cn(buttonVariants({ variant: resolvedVariant, size }), className, isCancel && "cancel-button")}
      ref={ref}
      {...props} />
  );
})
Button.displayName = "Button"

export { Button, buttonVariants }
